import type { CSSProperties } from 'react';

import type { SectionEdgeShape } from '@/features/storefront/sections/schema';

/**
 * A band's wavy or curved edge.
 *
 * Drawn *outside* the band, in the band's own background: a top edge stands
 * on the band's top and rises into the section above it, a bottom edge hangs
 * from its bottom into the one below. So the band itself keeps every pixel of
 * its padding, and a coloured band reads as having a soft edge whatever its
 * neighbour is - the page, another band, a photograph.
 *
 * One path per shape on a 1440 x 60 grid, stretched to the band's width with
 * `preserveAspectRatio="none"`: the edge always spans the band exactly, so it
 * can never push the page sideways. Its height follows the width (1:30, so
 * 48px on a laptop) with a floor that keeps a phone's edge visible.
 */

const PATHS: Record<Exclude<SectionEdgeShape, 'straight'>, string> = {
  // Two soft swells of different heights - a regular sine reads as a pattern
  // rather than as an edge.
  wave: 'M0 60 V34 C160 10 320 10 480 30 S800 56 960 36 S1280 8 1440 26 V60 Z',
  // One shallow arc over the whole width.
  curve: 'M0 60 Q720 -52 1440 60 Z',
};

const BASE: CSSProperties = {
  position: 'absolute',
  left: 0,
  display: 'block',
  width: '100%',
  height: 'auto',
  aspectRatio: '30 / 1',
  minHeight: '20px',
  maxHeight: '48px',
  // Over the neighbouring band, under anything that lifts itself (a sticky
  // header, a drawer, a popover).
  zIndex: 1,
  pointerEvents: 'none',
};

export function SectionEdge({
  shape,
  side,
  fill,
}: {
  shape: SectionEdgeShape;
  side: 'top' | 'bottom';
  /** The band's resolved background, as a CSS colour. */
  fill: string;
}) {
  if (shape === 'straight') return null;
  // One pixel of overlap, so no hairline of the neighbour shows through where
  // the edge meets the band at a fractional pixel.
  const style: CSSProperties =
    side === 'top'
      ? { ...BASE, bottom: 'calc(100% - 1px)' }
      : { ...BASE, top: 'calc(100% - 1px)', transform: 'scaleY(-1)' };
  return (
    <svg
      aria-hidden
      focusable="false"
      data-section-edge={`${side}-${shape}`}
      viewBox="0 0 1440 60"
      preserveAspectRatio="none"
      style={style}
    >
      <path d={PATHS[shape]} style={{ fill }} />
    </svg>
  );
}
